import React from 'react';
import { action, computed, makeObservable, observable } from 'mobx';
import { observer } from 'mobx-react';

import { IMouseEvent } from '../../../models/IMouseEvent.js';
import { ComponentBase } from '../../../shared/ComponentBase.js';
import { InjectedStyledComponent } from '../../../shared/InjectedStyledComponent.js';
import Button from '../Button/Button.js';
import { IIconProps } from '../Icon/models/index.js';
import { ITranslationContextValue } from '../Translate/models/ITranslationContextValue.js';
import { TranslationContext } from '../Translate/Translate.js';

import { ISortButtonConfiguration, ISortButtonProps, ISortButtonState, SortButtonDirection } from './models/index.js';
import Wrapper from './styledComponents/Wrapper.js';
import { SORT_BUTTON_CATEGORY_KEY, SORT_BUTTON_COMPONENT_KEY } from './SortButton.meta.js';
export class SortButton extends ComponentBase<ISortButtonProps, ISortButtonState, ISortButtonConfiguration> {
	categoryKey = SORT_BUTTON_CATEGORY_KEY;
	componentKey = SORT_BUTTON_COMPONENT_KEY;

	constructor(props: ISortButtonProps) {
		super(props);

		makeObservable<SortButton, 'direction'>(this, {
			payload: computed,
			handleToggle: action,
			setDirection: action,
			direction: observable.ref,
			iconProps: computed,
		});

		this.setDirection(props.direction);
	}

	get payload(): any {
		return this.props.payload;
	}

	componentDidUpdate(prevProps: ISortButtonProps) {
		if (prevProps.direction !== this.props.direction) {
			this.setDirection(this.props.direction);
		}
	}

	handleToggle = async (event: IMouseEvent) => {
		event.stopPropagation();
		if (this.direction === SortButtonDirection.unset) {
			this.direction = SortButtonDirection.ascending;
		} else if (this.direction === SortButtonDirection.ascending) {
			this.direction = SortButtonDirection.descending;
		} else if (this.direction === SortButtonDirection.descending) {
			this.direction = SortButtonDirection.unset;
		}
		const { onChange } = this.props;
		if (onChange) {
			await onChange(this.props.payload, this.direction);
		}
	};

	setDirection = (direction?: SortButtonDirection) => {
		this.direction = direction || SortButtonDirection.unset;
	};

	private direction: SortButtonDirection = SortButtonDirection.unset;

	get iconLoadingDefault(): IIconProps {
		return {
			asset: 'base.sortButton.loading',
			className: 'ksys-sort-button-loading',
			accessibilityText: 'loading',
		};
	}

	get iconAscendingDefault(): IIconProps {
		return {
			asset: 'base.sortButton.ascending',
		};
	}

	get iconDescendingDefault(): IIconProps {
		return {
			asset: 'base.sortButton.descending',
		};
	}

	get iconUnsetDefault(): IIconProps {
		return {
			asset: 'base.sortButton.unset',
		};
	}

	get iconProps(): IIconProps {
		const { loading = false, iconAscending, iconDescending, iconUnset, iconLoading } = this.props;
		if (loading) {
			return iconLoading || this.iconLoadingDefault;
		}
		if (this.direction === SortButtonDirection.ascending) {
			return iconAscending || this.iconAscendingDefault;
		}
		if (this.direction === SortButtonDirection.descending) {
			return iconDescending || this.iconDescendingDefault;
		}
		return iconUnset || this.iconUnsetDefault;
	}

	/**
	 * Returns a string that represents the accessibility label for the button.
	 * It provides a description of the next sorting state of the button.
	 * @returns A string representing the accessibility label for the button.
	 */
	get ariaLabel(): string {
		switch (this.direction) {
			case SortButtonDirection.unset:
				return 'Sort in ascending order';
			case SortButtonDirection.ascending:
				return 'Sort in descending order';
			case SortButtonDirection.descending:
				return 'Unset the sort';
			default:
				return 'Sort by custom';
		}
	}

	render() {
		const { className, buttonProps = {}, loading = false } = this.props;
		const iconProps = this.iconProps;
		return (
			<Wrapper className={className}>
				<TranslationContext.Consumer>
					{(translationContext: ITranslationContextValue) => {
						return (
							<Button
								{...buttonProps}
								disabled={loading}
								onClick={this.handleToggle}
								iconProps={iconProps}
								aria-label={translationContext.translate(this.ariaLabel)}
							/>
						);
					}}
				</TranslationContext.Consumer>
			</Wrapper>
		);
	}
}

export default InjectedStyledComponent(observer(SortButton), SORT_BUTTON_CATEGORY_KEY, SORT_BUTTON_COMPONENT_KEY);
